import { EventEmitter } from "@angular/core";
import { Component, Inject } from "@angular/core";
import { Toast } from "src/app/ui/components/toast/toast";
import { JobDescription } from "src/app/ui/models/domains/pbm/Beneficiary/job-description";
import { Company } from "src/app/ui/models/domains/pbm/companies/company";
import { LoadingService } from "src/app/ui/services/loading.service";
import { JobDescriptionService } from "src/app/ui/services/pbm/job-description.service";
import { JobDescriptionDialogType } from "./add-job-description-dialog.enum";
import { MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";

@Component({
  templateUrl: "./add-job-description-dialog.component.html",
  styleUrls: ["./add-job-description-dialog.component.scss"],
})
export class AddJobDescriptionDialog {
  public closeDialogEvent: EventEmitter<JobDescription> = new EventEmitter();

  jobDescriptionDialogStrategy: JobDescriptionDialogType;
  company: Company;
  jobDescriptionList: JobDescription[];
  jobDescriptionTitle: string = "";
  jobDescriptionEnabled: boolean;
  jobDescriptionSelected: JobDescription;
  beneficiaryJobDescription: JobDescription;
  headerManager: JobDescriptionDialogHeaderManager = new JobDescriptionDialogHeaderManager();

  constructor(
    public dialogRef: MatDialogRef<AddJobDescriptionDialog>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private jobDescriptionService: JobDescriptionService,
    private loadingService: LoadingService,
    private toast: Toast,
  ) {
    this.jobDescriptionDialogStrategy = this.data.strategy;
    this.jobDescriptionList = this.data.jobDescriptionList;
    
    this.company = Company.fromObject(this.data.company);
  }

  public async getJobDescription() {
    this.jobDescriptionList = await this.jobDescriptionService.getJobDescription(this.company.getFiscalNumber());
  }

  public async createJobDescription() {
    if (this.jobDescriptionTitle) {
      const loadingRef = this.loadingService.beginLoading();

      try {
        const jobDescription = new JobDescription("", this.jobDescriptionTitle, false, true);
        await this.jobDescriptionService.createJobDescription(jobDescription, this.company.getFiscalNumber());
        await this.getJobDescription();
        this.jobDescriptionTitle = "";
      } catch (error) {
        this.toast.showWarningToast(error.error.Data.ErrorMessage);
      } finally {
        loadingRef.closeLoading();
      }
    } else {
      this.toast.showWarningToast("Por favor, informe o nome do cargo");
    }
  }

  public async editJobDescription() {
    const loadingRef = this.loadingService.beginLoading();

    try {
      const jobDescription = new JobDescription(this.jobDescriptionSelected.getKey(), this.jobDescriptionTitle, false, this.jobDescriptionEnabled);
      
      await this.jobDescriptionService.editJobDescription(jobDescription, this.company.getFiscalNumber());
      await this.getJobDescription();
      this.jobDescriptionTitle = "";
      this.onCancelEditingJobDescription();
    } catch (error) {
      this.toast.showWarningToast(error.error.Data.ErrorMessage);
    } finally {
      loadingRef.closeLoading();
    }
  }

  public onDeleteJobDescription(jobDescription: JobDescription): void {
    const deleteJob = confirm("Deseja excluir este cargo?");

    if (deleteJob) {
      this.deleteJobDescription(jobDescription);
    }
  }

  private async deleteJobDescription(jobDescription: JobDescription) {
    const loadingRef = this.loadingService.beginLoading();

    try {
      await this.jobDescriptionService.deleteJobDescription(jobDescription.getKey(), this.company.getFiscalNumber());
      await this.getJobDescription();
    } catch (error) {
      this.toast.showWarningToast(error.error.Data.ErrorMessage);
    } finally {
      loadingRef.closeLoading();
    }
  }

  public closeDialog(): void {
    this.closeDialogEvent.emit(this.beneficiaryJobDescription);
    this.dialogRef.close();
  }

  public hasJobDescriptionSelected(): boolean {
    return !!this.jobDescriptionSelected;
  }

  public onAddingJobDescription() {
    this.headerManager.setInitialized(true);
    this.headerManager.setAdding(true);
    this.headerManager.setEditing(false);
  }

  public onEditingJobDescription(jobDescription: JobDescription): void {
    this.headerManager.setInitialized(true);
    this.headerManager.setAdding(false);
    this.headerManager.setEditing(true);

    this.jobDescriptionSelected = jobDescription;
    this.jobDescriptionTitle = this.jobDescriptionSelected.getTitle();
    this.jobDescriptionEnabled = this.jobDescriptionSelected.getEnabled();
  }

  public onCancelEditingJobDescription(): void {
    this.onAddingJobDescription();
    this.jobDescriptionTitle = "";
    this.jobDescriptionSelected = null;
  }

  public showInitHeader(): boolean {
    return !this.headerManager.isInitialized() && !this.headerManager.isAdding() && !this.headerManager.isEditing();
  }

  public showAddHader(): boolean {
    return this.headerManager.isInitialized() && this.headerManager.isAdding() && !this.headerManager.isEditing();
  }

  public showEditHader(): boolean {
    return this.headerManager.isInitialized() && !this.headerManager.isAdding() && this.headerManager.isEditing();
  }

  public isBeneficiaryStrategy(): boolean {
    return this.jobDescriptionDialogStrategy === JobDescriptionDialogType.Beneficiary;
  }

  public onSelectJobDescription(jobDescription: JobDescription) {
    this.beneficiaryJobDescription = jobDescription;
    this.closeDialog();
  }
}

class JobDescriptionDialogHeaderManager {
  private initialized: boolean;
  private adding: boolean;
  private editing: boolean;

  setInitialized(value: boolean): void {
    this.initialized = value;
  }

  isInitialized(): boolean {
    return this.initialized;
  }

  setAdding(value: boolean): void {
    this.adding = value;
  }

  isAdding(): boolean {
    return this.adding;
  }

  setEditing(value: boolean): void {
    this.editing = value;
  }

  isEditing(): boolean {
    return this.editing;
  }
}
